import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Button, FormGroup } from '@mui/material';

import { SharedComponentSet } from '@paragon/contract-library';
import { designService, sealingService, userService } from '@paragon/data-connections';
import { getClientUrls, isLoggedInUserEngineer } from '@paragon/login-tools';
import { enqueueWarning } from '@paragon/notification-tools';
import { UserDropdown } from '@paragon/themes';

import { showHelpPopup } from '../../actions/popup';
import { RootState } from '../../store';
import LockTool from '../Tools/LockTool';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

export class UnconnectedHeaderTools extends React.Component<Props> {
  onClickViewComponent = async () => {
    const componentDesignPromise = sealingService.componentDesigns.get(this.props.activeComponentDesignId!);

    // optimistically get the organization link GUID for the organization that created the job in case it is needed
    const jobOrganizationPromise = userService.organizations.getLinkGuid(this.props.jobOrganization);

    // we will need the shared component set used when creating the job in order to link to it
    let sharedComponentSetPromise: Promise<SharedComponentSet | null> = Promise.resolve(null);
    if (this.props.jobComponentSetCode) {
      sharedComponentSetPromise = designService.sharedComponentSets.fetch(this.props.jobComponentSetCode);
    }

    const componentDesign = await componentDesignPromise;
    const sharedComponentSet = await sharedComponentSetPromise;

    const designServiceComponentGuid = componentDesign.analysisRemoteKey;
    if (designServiceComponentGuid == null) {
      this.props.enqueueWarning(
        'There is no design for this component. It may still be processing, or there was an error in processing it.',
      );
      return;
    }

    if (sharedComponentSet != null) {
      window.open(
        `${this.props.designUrl}${sharedComponentSet.lookupCode}/${designServiceComponentGuid}/componentDesign/${designServiceComponentGuid}/0`,
        '_blank',
      );
    } else {
      // fallback behavior (how it always used to work before shared component sets were added) -- create an old-style
      //   TDD link using organization guid users can view the component as if they had clicked a link on its TDD
      const jobOrganization = await jobOrganizationPromise;
      window.open(
        `${this.props.designUrl}${this.props.loggedInUserIsEngineer ? this.props.jobId : 0}/${designServiceComponentGuid}/componentDesign/${designServiceComponentGuid}/0?organizationGuid=${jobOrganization.linkGuid}&customerGuid=${jobOrganization.linkGuid}`,
        '_blank',
      );
    }
  };

  render() {
    const { jobIsLocked } = this.props;

    const componentExists = this.props.activeComponentDesignId != null && this.props.componentViewableInDesign;
    const componentButton = componentExists && (
      <Button variant="contained" sx={{ m: 1 }} id="view-component" onClick={this.onClickViewComponent}>
        View Component
      </Button>
    );

    return (
      <FormGroup row sx={{ alignItems: 'center' }}>
        {componentButton}
        <LockTool isLocked={jobIsLocked} />
        <UserDropdown />
      </FormGroup>
    );
  }
}

function mapStateToProps(state: RootState) {
  let activeComponentDesignId = null;
  let componentViewableInDesign = false;

  const currentDesignData = state.documents.pdfData[state.documents.activeDocumentId!];
  if (typeof currentDesignData === 'object' && currentDesignData.index != null) {
    activeComponentDesignId = currentDesignData.componentDesignId;
    componentViewableInDesign = currentDesignData.designServiceComponentId != null;
  }

  return {
    jobId: state.job.id,
    jobIsLocked: state.job.isLocked,
    jobOrganization: state.job.ownerOrganization,
    jobComponentSetCode: state.job.sharedComponentSetCode,

    activeComponentDesignId,
    componentViewableInDesign,

    designUrl: getClientUrls(state).designUrl,
    loggedInUserIsEngineer: isLoggedInUserEngineer(state),
  };
}

const actionCreators = {
  enqueueWarning,
  showHelpPopup,
};

export default connect(mapStateToProps, actionCreators)(UnconnectedHeaderTools);
